import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@nextui-org/react";
import { realtimeBiometricsCheck } from "../../../store/verification.slice";
import { useSelector } from "react-redux";
import { IS_IOS } from "../../utils";
import { trackEvent } from "../../../utils/analytics";

const SelfieCamera = ({ onCapture, uploading, clientId, flowId, verificationId }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const livenessIntervalRef = useRef(null);
  const isCheckingLivenessRef = useRef(false);
  const promptSmileRef = useRef(false);

  // States for the flow
  const [promptSmile, setPromptSmile] = useState(false);
  const [isCheckingLiveness, setIsCheckingLiveness] = useState(false);

  const [isCentered, setIsCentered] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [screenshotTaken, setScreenshotTaken] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [faceLandmarkerReady, setFaceLandmarkerReady] = useState(false);
  const [lightingQuality, setLightingQuality] = useState("checking");
  const [faceVisibility, setFaceVisibility] = useState("checking");
  const { credentials } = useSelector((store) => store.verification);

  const isCapturingRef = useRef(false);
  const lastVideoTimeRef = useRef(-1);

  const LIVENESS_CHECK_INTERVAL = 1000; // Send a screenshot every 1 second

  // Initialize MediaPipe FaceLandmarker
  useEffect(() => {
    const initializeFaceLandmarker = async () => {
      try {
        const waitForMediaPipe = () =>
          new Promise((resolve) => {
            const check = () =>
              window.MediaPipeVision ? resolve() : setTimeout(check, 100);
            check();
          });

        await waitForMediaPipe();
        const { FaceLandmarker, FilesetResolver } = window.MediaPipeVision;
        const filesetResolver = await FilesetResolver.forVisionTasks(
          "/mediapipe/wasm"
        );

        const faceLandmarker = await FaceLandmarker.createFromOptions(
          filesetResolver,
          {
            baseOptions: {
              modelAssetPath: "/mediapipe/models/face_landmarker.task",
              delegate: "GPU",
            },
            outputFaceBlendshapes: false,
            runningMode: "VIDEO",
            numFaces: 1,
          }
        );

        faceLandmarkerRef.current = faceLandmarker;
        setFaceLandmarkerReady(true);
      } catch (error) {
        console.error("Error initializing FaceLandmarker:", error);
      }
    };
    initializeFaceLandmarker();
    return () => {
      if (faceLandmarkerRef.current) faceLandmarkerRef.current.close();
    };
  }, []);

  const takeScreenshot = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || videoRef.current.paused)
      return null;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext("2d");
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return null;

    canvas.width = videoWidth;
    canvas.height = videoHeight;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!screenshotTaken) {
            const url = URL.createObjectURL(blob);
            setScreenshotUrl(url);
          }
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  }, [screenshotTaken]);

  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const uploadScreenshot = useCallback(
    (blob) => {
      setScreenshotTaken(true);
      setIsCapturing(false);
      onCapture({ type: "image", value: blob });
    },
    [onCapture]
  );

  /**
   * This function is called by the interval. It takes a screenshot, sends it to the API,
   * and handles the success case.
   */

  const performLivenessCheck = useCallback(async () => {
    if (!credentials?.accessToken) {
      return;
    }

    if (
      isCheckingLivenessRef.current ||
      !videoRef.current ||
      videoRef.current.paused
    ) {
      return;
    }

    isCheckingLivenessRef.current = true;
    setIsCheckingLiveness(true);

    const blob = await takeScreenshot();

    if (blob) {
      try {
        const response = await realtimeBiometricsCheck(
          blob,
          "SELFIE_PHOTO",
          "SMILE",
          clientId,
          credentials.accessToken
        );

        if (response.status === 200 && response.data.is_live) {
          if (livenessIntervalRef.current) {
            clearInterval(livenessIntervalRef.current);
            livenessIntervalRef.current = null;
          }
          uploadScreenshot(blob);
          stopCamera();
        } else {
          isCheckingLivenessRef.current = false;
          setIsCheckingLiveness(false);
        }
      } catch (error) {
        console.error("💥 Liveness API call failed:", error);
        isCheckingLivenessRef.current = false;
        setIsCheckingLiveness(false);
      }
    } else {
      isCheckingLivenessRef.current = false;
      setIsCheckingLiveness(false);
    }
  }, [clientId, credentials, takeScreenshot, uploadScreenshot, stopCamera]);

  // Logic to check if face is centered - simplified to just trigger smile prompt
  const checkCentered = useCallback(
    (
      yaw,
      pitch,
      insideCircle,
      lightingGood,
      visibilityGood,
      isCurrentlyCapturing
    ) => {
      if (promptSmileRef.current || !isCurrentlyCapturing || screenshotTaken)
        return;

      const centered =
        Math.abs(yaw) < 15 &&
        Math.abs(pitch) < 15 &&
        insideCircle &&
        lightingGood &&
        visibilityGood;

      if (centered) {
        setIsCentered(true);
        // Immediately prompt for smile when centered
        if (!promptSmileRef.current) {
          promptSmileRef.current = true;
          setPromptSmile(true);
        }
      } else {
        setIsCentered(false);
        // If user moves out of center, stop liveness checks
        if (promptSmileRef.current) {
          promptSmileRef.current = false;
          setPromptSmile(false);
          if (livenessIntervalRef.current) {
            clearInterval(livenessIntervalRef.current);
            livenessIntervalRef.current = null;
          }
        }
      }
    },
    [screenshotTaken]
  );

  // processVideoFrame only handles centering and quality checks now
  const processVideoFrame = useCallback(async () => {
    const video = videoRef.current;
    const faceLandmarker = faceLandmarkerRef.current;
    if (
      !video ||
      !faceLandmarker ||
      !faceLandmarkerReady ||
      video.readyState < 2
    ) {
      if (isCapturingRef.current) requestAnimationFrame(processVideoFrame);
      return;
    }

    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const results = faceLandmarker.detectForVideo(video, performance.now());
        if (results.faceLandmarks && results.faceLandmarks.length > 0) {
          const landmarks = results.faceLandmarks[0];
          const { lighting, visibility } = analyzeLightingAndQuality(
            landmarks,
            video.videoWidth,
            video.videoHeight
          );
          setLightingQuality(lighting);
          setFaceVisibility(visibility);
          const { yaw, pitch } = calculateHeadPose(landmarks);
          const noseTip = landmarks[1];
          const noseX = noseTip.x * video.videoWidth;
          const noseY = noseTip.y * video.videoHeight;
          const cx = video.videoWidth / 2;
          const cy = video.videoHeight / 2;
          const r = Math.min(video.videoWidth, video.videoHeight) * 0.25;
          const insideCircle =
            Math.sqrt((noseX - cx) ** 2 + (noseY - cy) ** 2) < r * 0.8;
          checkCentered(
            yaw,
            pitch,
            insideCircle,
            lighting === "good",
            visibility === "visible",
            isCapturingRef.current
          );
        } else {
          setLightingQuality("poor");
          setFaceVisibility("poor");
        }
      } catch (error) {
        console.error("Error processing video frame:", error);
      }
    }
    if (isCapturingRef.current) requestAnimationFrame(processVideoFrame);
  }, [faceLandmarkerReady, checkCentered]);

  const analyzeLightingAndQuality = (landmarks, videoWidth, videoHeight) => {
    if (!landmarks || landmarks.length === 0)
      return { lighting: "poor", visibility: "poor" };
    const facePoints = landmarks.slice(0, 468);
    const xs = facePoints.map((p) => p.x * videoWidth);
    const ys = facePoints.map((p) => p.y * videoHeight);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const faceWidth = maxX - minX;
    const faceHeight = maxY - minY;
    const faceSizeRatio = (faceWidth * faceHeight) / (videoWidth * videoHeight);
    const goodFaceSize = faceSizeRatio > 0.08 && faceSizeRatio < 0.4;
    const eyePoints = [
      landmarks[33],
      landmarks[7],
      landmarks[163],
      landmarks[144],
      landmarks[362],
      landmarks[398],
      landmarks[384],
      landmarks[385],
    ];
    const nosePoints = [
      landmarks[1],
      landmarks[2],
      landmarks[5],
      landmarks[19],
    ];
    const eyeSpread =
      Math.max(...eyePoints.map((p) => p.x)) -
      Math.min(...eyePoints.map((p) => p.x));
    const noseSpread =
      Math.max(...nosePoints.map((p) => p.y)) -
      Math.min(...nosePoints.map((p) => p.y));
    const goodFeatureDefinition = eyeSpread > 0.05 && noseSpread > 0.03;
    const lightingQuality = goodFeatureDefinition ? "good" : "poor";
    const visibilityQuality =
      goodFaceSize && goodFeatureDefinition ? "visible" : "poor";
    return { lighting: lightingQuality, visibility: visibilityQuality };
  };

  const calculateHeadPose = (landmarks) => {
    if (!landmarks || landmarks.length === 0) return { yaw: 0, pitch: 0 };
    const noseTip = landmarks[1];
    const leftEyeCorner = landmarks[33];
    const rightEyeCorner = landmarks[263];
    const chinPoint = landmarks[18];
    const foreheadCenter = landmarks[9];
    const eyeDistance = Math.abs(rightEyeCorner.x - leftEyeCorner.x);
    const leftEyeToNose = Math.abs(noseTip.x - leftEyeCorner.x);
    const rightEyeToNose = Math.abs(noseTip.x - rightEyeCorner.x);
    const yawRatio = (leftEyeToNose - rightEyeToNose) / eyeDistance;
    const yaw = yawRatio * 100;
    const faceHeight = Math.abs(foreheadCenter.y - chinPoint.y);
    const noseToTop = Math.abs(noseTip.y - foreheadCenter.y);
    const noseToBottom = Math.abs(chinPoint.y - noseTip.y);
    const pitchRatio = (noseToTop - noseToBottom) / faceHeight;
    const pitch = pitchRatio * 100;
    return { yaw, pitch };
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          facingMode: "user",
          frameRate: { ideal: 30 },
          exposureMode: { ideal: "continuous" },
          whiteBalanceMode: { ideal: "continuous" },
        },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          if (isCapturingRef.current) {
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current);
            }
            animationFrameRef.current =
              requestAnimationFrame(processVideoFrame);
          }
        };
      }
    } catch (error) {
      console.error("Error starting camera:", error);
    }
  };

  // startCamera is redefined every render (it closes over processVideoFrame,
  // which itself changes identity with faceLandmarkerReady/checkCentered), so
  // the visibilitychange listener below — registered once — reads it through
  // this ref rather than closing over a single, possibly stale, render's copy.
  const startCameraRef = useRef(startCamera);
  useEffect(() => {
    startCameraRef.current = startCamera;
  });

  useEffect(() => {
    if (faceLandmarkerReady && !screenshotTaken) {
      trackEvent("liveness_recording_started", { flowId, verificationId });
      setIsCapturing(true);
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceLandmarkerReady, screenshotTaken]);

  // The camera can be interrupted by backgrounding the app (lock screen, app
  // switch): a dead/muted track leaves a frozen feed, and on iOS (Safari and
  // every other iOS browser, since they all run on WebKit) the video element
  // can paint a stale/blank frame on resume even while the track itself still
  // reports readyState "live" and muted: false. Mirrors the fix in
  // DocumentCamera.jsx.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (screenshotTaken) return;
      const track = videoRef.current?.srcObject?.getVideoTracks?.()?.[0];
      const trackUnhealthy = track && (track.readyState !== "live" || track.muted);
      if (trackUnhealthy || IS_IOS) {
        stopCamera();
        startCameraRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [screenshotTaken, stopCamera]);

  useEffect(() => {
    isCapturingRef.current = isCapturing;
  }, [isCapturing]);

  useEffect(() => {
    isCheckingLivenessRef.current = isCheckingLiveness;
  }, [isCheckingLiveness]);

  useEffect(() => {
    promptSmileRef.current = promptSmile;
  }, [promptSmile]);


  // Start/stop liveness checks based on promptSmile state
  useEffect(() => {
    // Only start interval if we don't already have one running
    if (promptSmile && !screenshotTaken && !livenessIntervalRef.current) {
       livenessIntervalRef.current = setInterval(
        performLivenessCheck,
        LIVENESS_CHECK_INTERVAL
      );
    }

    // Only clear interval if promptSmile becomes false (not on every render)
    if (!promptSmile && livenessIntervalRef.current) {
      clearInterval(livenessIntervalRef.current);
      livenessIntervalRef.current = null;
    }

    return () => {
      // Only cleanup on unmount, not on every promptSmile change
      if (!promptSmile && livenessIntervalRef.current) {
        clearInterval(livenessIntervalRef.current);
        livenessIntervalRef.current = null;
      }
    };
  }, [promptSmile, screenshotTaken, performLivenessCheck]);

  const getInstructionText = () => {
    if (!faceLandmarkerReady) return t("liveness.selfieCamera.loading");
    if (screenshotTaken) return t("liveness.selfieCamera.captureSuccessful");
    if (promptSmile) return t("liveness.selfieCamera.smilePrompt");
    if (lightingQuality === "poor") return t("liveness.selfieCamera.improveLighting");
    if (faceVisibility === "poor")
      return t("liveness.selfieCamera.faceNotVisible");
    if (isCentered) return t("liveness.selfieCamera.centeredSmile");
    return t("liveness.selfieCamera.centerFace");
  };

  const ringColor = screenshotTaken || (lightingQuality === "good" && faceVisibility === "visible")
    ? "#22c55e"
    : lightingQuality === "poor" || faceVisibility === "poor"
    ? "#ef4444"
    : "#d1d5db";

  return (
    <div className="flex flex-col h-full">

      {/* Camera circle — centred, takes up most of the height */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
        <div className="relative" style={{ width: "80%", aspectRatio: "1 / 1" }}>
          {/* Ring border */}
          <div
            className="absolute inset-0 rounded-full transition-colors duration-500"
            style={{ background: ringColor, padding: 6 }}
          >
            <div className="w-full h-full rounded-full overflow-hidden bg-white">
              {screenshotTaken && screenshotUrl ? (
                <img
                  src={screenshotUrl}
                  alt={t("liveness.selfieCamera.capturedAlt")}
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  playsInline
                  style={{ transform: "scaleX(-1)" }}
                />
              )}
            </div>
          </div>

          {/* Uploading spinner */}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <CircularProgress
                size="sm"
                color="success"
                classNames={{
                  svg: "w-full h-full",
                  track: "stroke-white/10",
                  indicator: "stroke-green-500",
                }}
                aria-label="uploading..."
              />
            </div>
          )}
        </div>

        {/* Instructions directly below circle */}
        <div className="text-center flex flex-col items-center gap-2">
          <div className="heading text-lg">{t("liveness.keepFaceInOval")}</div>
          <div className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
            isCapturing
              ? "bg-blue-500 text-white shadow-md"
              : "bg-gray-100 text-gray-500"
          }`}>
            {getInstructionText()}
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default SelfieCamera;
